import * as React from 'react'

import { action, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import DeliveryActionTypes from '~/client/src/desktop/enums/DeliveryActionTypes'
import { Loader } from '~/client/src/shared/components/Loader'
import MapViewsGallery from '~/client/src/shared/components/SitemapsGallery/MapViewsGallery'
import ICanvasImageCache from '~/client/src/shared/interfaces/ITextboxesCache'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GlobeView from '~/client/src/shared/models/GlobeView'
import Sitemap from '~/client/src/shared/models/Sitemap'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import { SAVE_DELIVERY_CONFIGURATIONS } from '~/client/src/shared/stores/EventStore/eventConstants'

import DeliveriesMapSidebar from './components/DeliveriesMapSidebar/DeliveriesMapSidebar'
import DeliveryMapView from './components/DeliveryMapView/DeliveryMapView'
import DeliveriesMapStore from './DeliveriesMap.store'

// localization: translated
interface IProps {
  store: DeliveriesMapStore

  activeActionType: DeliveryActionTypes
  onNewDeliveryCreate: (
    columnFields?: any,
    startDate?: Date,
    endDate?: Date,
    zoneId?: string,
  ) => void
  globeViewsStore?: GlobeViewsStore
  sitemapsStore?: SitemapsStore
}

@inject('globeViewsStore', 'sitemapsStore')
@observer
export default class DeliveriesMap extends React.Component<IProps> {
  private textboxesCache: ICanvasImageCache = {}
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props

    props.store.setGroupingFilter()

    const config = props.store.appState.deliveriesMapIdsList
    const isGlobeMode = !!config?.[0]?.globeViewId
    if (isGlobeMode) {
      const globe = props.globeViewsStore.byId.get(config?.[0]?.globeViewId)
      this.localProps.store.globeViewControlStore.selectGlobe(globe)
    } else {
      const whiteboard = props.sitemapsStore.byId.get(config?.[0]?.sitemapId)
      this.localProps.store.selectSitemap(whiteboard)
      this.localProps.store.mapBoxViewerStore.setViewportFromAddress()
    }
    makeObservable(this)
  }

  public componentDidUpdate(): void {
    runInAction(() => {
      if (this.localProps.activeActionType !== this.props.activeActionType)
        this.localProps.activeActionType = this.props.activeActionType
    })
  }

  public render() {
    const { store } = this.props

    return (
      <div className="row full-height bt-light-cool-grey">
        <DeliveriesMapSidebar
          listStore={store.deliveriesBySitemapListStore}
          mapStore={store}
        />
        {this.renderContent()}
      </div>
    )
  }

  public componentDidMount() {
    this.localProps.store.mount()
  }

  public componentWillUnmount() {
    this.localProps.store.unmount()
    this.textboxesCache = {}
  }

  public renderContent() {
    const {
      store,
      store: { appState },
    } = this.localProps
    const { isInitialLoaderShown } = store

    if (isInitialLoaderShown) {
      return (
        <Loader hint={Localization.translator.sitemapsDataIsLoading + '...'} />
      )
    }

    return (
      <div className="col full-height">
        <div className="full-height relative">
          <MapViewsGallery
            mapIds={appState.deliveriesMapIdsList}
            selectedGlobeViewId={
              store.globeViewControlStore.selectedGlobeView?.id
            }
            selectedWhiteboardId={store.baseSitemapSetUpStore.sitemap?.id}
            renderMapViewComponent={this.renderMapView}
            shouldUseFullHeight={true}
            mapBoxViewerStore={store.mapBoxViewerStore}
            eventName={SAVE_DELIVERY_CONFIGURATIONS}
            selectGlobe={this.selectGlobeAndCloseModals}
            selectSitemap={this.selectSitemapAndCloseModals}
            shouldDisableZoom={true}
          />
        </div>
      </div>
    )
  }

  private renderMapView = (): JSX.Element => {
    const { store } = this.localProps
    return (
      <DeliveryMapView
        globe={store.globeViewControlStore.selectedGlobeView}
        sitemap={store.baseSitemapSetUpStore.sitemap}
        deliveriesMapStore={store}
        textboxesCache={this.textboxesCache}
      />
    )
  }

  @action.bound
  private selectGlobeAndCloseModals(globe: GlobeView) {
    this.localProps.store.deselectSitemap()
    this.localProps.store.selectGlobe(globe)

    this.localProps.store.mapBoxViewerStore.setViewportFromAddress()
  }

  @action.bound
  private selectSitemapAndCloseModals(sitemap: Sitemap) {
    this.localProps.store.deselectGlobe()
    this.localProps.store.selectSitemap(sitemap)

    this.localProps.store.mapBoxViewerStore.setViewportFromAddress()
  }
}
